import { Markdown, type MarkdownSize } from '@highlight/ui'
import { BriefCitations } from '../components/brief-citations'
import type { BriefLinkPreview, BriefStructure } from '../types'

interface BriefSummarySectionProps {
  markdown: string
  /**
   * The v2 brief. Renders instead of the markdown, which is flattened from
   * these same items, and carries the citations behind each bullet.
   */
  structure?: BriefStructure
  previews?: Record<string, BriefLinkPreview>
  size?: MarkdownSize
}

export function BriefSummarySection({ markdown, structure, previews, size }: BriefSummarySectionProps) {
  if (structure) {
    return <BriefStructureBody structure={structure} previews={previews} size={size} />
  }
  if (!markdown.trim()) return null
  return <Markdown size={size}>{markdown}</Markdown>
}

/**
 * Mirrors what `Markdown` emits for the flattened brief, a paragraph followed
 * by a `ul`, inside the same `Markdown.Prose` root, so both paths style identically
 * and switching between them can't shift the layout. Not a `Markdown`: each
 * bullet carries React citations a markdown string can't hold.
 */
function BriefStructureBody({
  structure,
  previews,
  size,
}: {
  structure: BriefStructure
  previews?: Record<string, BriefLinkPreview>
  size?: MarkdownSize
}) {
  return (
    <Markdown.Prose size={size}>
      {structure.headline.trim() && <p>{structure.headline}</p>}
      {structure.bullets.length > 0 && (
        <ul>
          {/* Index keys: one saved brief is a fixed, ordered list, and two
              bullets can legitimately share text. */}
          {structure.bullets.map((bullet, index) => (
            <li key={index}>
              {bullet.text}
              <BriefCitations links={bullet.links} support={bullet.support} previews={previews} />
            </li>
          ))}
        </ul>
      )}
    </Markdown.Prose>
  )
}
